import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import WIcon from '../../components/shared/WIcon';

const initialsFor = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const CompanySettings = () => {
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', address: '', phone: '', email: '', website: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const stored = localStorage.getItem('companyData');
    if (!stored) { navigate('/company/login'); return; }
    const company = JSON.parse(stored);
    setCompanyData(company);
    setFormData({
      name: company.name || '',
      description: company.description || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      website: company.website || '',
    });
  }, [navigate]);

  const handleChange = (e) => {
    setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (message.text) setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await updateDoc(doc(db, 'companies', companyData.id), {
        ...formData,
        updatedAt: new Date(),
      });

      const next = { ...companyData, ...formData, updatedAt: new Date() };
      localStorage.setItem('companyData', JSON.stringify(next));
      setCompanyData(next);
      setMessage({ type: 'success', text: 'Settings updated successfully.' });
    } catch (err) {
      console.error('Error updating settings:', err);
      setMessage({ type: 'error', text: 'Failed to update settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const accountCreated = companyData?.createdAt?.seconds
    ? new Date(companyData.createdAt.seconds * 1000).toLocaleDateString()
    : '—';

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* Form */}
      <div className="loysta-card" style={{ flex: 1, minWidth: 0, padding: 0 }}>
        <div className="loysta-card-head">
          <span className="loysta-card-title">Company information</span>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {message.text && (
            <div className={`loysta-alert ${message.type === 'success' ? 'success' : 'error'}`}>
              {message.text}
            </div>
          )}

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Profile
            </div>
            <div className="form-group">
              <label>Company Name *</label>
              <input
                className="form-control"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Acme Corporation"
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of your company"
                rows={3}
              />
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Contact
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label>Email</label>
                <input
                  className="form-control"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="company@example.com"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  className="form-control"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+995 5XX XXX XXX"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Address</label>
              <textarea
                className="form-control"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Company address"
                rows={2}
              />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                className="form-control"
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.company.com"
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ minWidth: 160, justifyContent: 'center' }}
            >
              {loading ? 'Saving…' : (
                <>
                  <WIcon name="check" size={14} color="white" />
                  Save changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Right meta */}
      <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="loysta-card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #00bf63, #c1ff72)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 800, color: '#0f172a', flexShrink: 0,
            }}>{initialsFor(companyData?.name)}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {companyData?.name || '—'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Company workspace</div>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
            <Row label="Total employees" value={companyData?.employeeCount ?? 0} />
            <Row label="Company ID" value={companyData?.id?.slice(-8) || '—'} mono />
            <Row label="Account created" value={accountCreated} />
          </div>
        </div>

        <div className="loysta-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
            Admin account
          </div>
          <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
            <Row
              label="Admin email"
              value={companyData?.adminData?.email || '—'}
              mono
            />
            <Row label="Sign-in" value="Google SSO" />
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.55, marginTop: 14, marginBottom: 0 }}>
            Need to change the admin email? Contact your platform administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ color: '#94a3b8' }}>{label}</span>
    <span style={{
      color: '#0f172a',
      fontWeight: 600,
      fontFamily: mono ? 'SF Mono, ui-monospace, Menlo, monospace' : 'inherit',
      fontSize: mono ? 12 : 13,
      maxWidth: 180,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}>{value}</span>
  </div>
);

export default CompanySettings;
